import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import './MixpediaHint.scss';

const MixpediaHint = ({ targetRef, title, message, onDismiss, autoShow, actionLabel, onAction }) => {
    const hintRef = useRef(null);
    const [pos, setPos] = useState(null);
    const [visible, setVisible] = useState(!!autoShow);
    const hideTimer = useRef(null);

    useEffect(() => {
        const el = targetRef?.current;
        if (!el) return;

        const update = () => {
            const rect = el.getBoundingClientRect();
            // Only while the button itself is on screen: the page under a
            // collection/DJ page (or a hidden tab) stays mounted, and its hint
            // used to float over whatever covered it.
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const hit = rect.width > 0 && cy >= 0 && cy <= window.innerHeight && cx >= 0 && cx <= window.innerWidth
                ? document.elementFromPoint(cx, cy) : null;
            const onScreen = !!hit && (el.contains(hit) || !!hit.closest('.mixpedia-hint, .mixpedia-beacon'));
            if (!onScreen) { setPos(null); return; }
            setPos(prev => {
                if (prev && prev.left === rect.left && prev.top === rect.top && prev.right === rect.right && prev.bottom === rect.bottom) return prev;
                return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height };
            });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        // Poll position so the dot tracks the button when sidebars open/close
        const interval = setInterval(update, 150);

        const show = () => { clearTimeout(hideTimer.current); setVisible(true); };
        const hide = () => { hideTimer.current = setTimeout(() => setVisible(false), 150); };
        el.addEventListener('mouseenter', show);
        // In beacon-only mode, hover toggles the tooltip. When auto-shown, user must click Got it.
        if (!autoShow) el.addEventListener('mouseleave', hide);

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
            clearInterval(interval);
            el.removeEventListener('mouseenter', show);
            if (!autoShow) el.removeEventListener('mouseleave', hide);
            clearTimeout(hideTimer.current);
        };
    }, [targetRef]);

    useEffect(() => {
        if (!hintRef.current || !visible) return;
        gsap.fromTo(hintRef.current,
            { opacity: 0, y: -10, scale: 0.9 },
            { opacity: 1, y: 0, scale: 1, duration: 0.3, ease: 'back.out(1.7)' }
        );
    }, [visible]);

    if (!pos) return null;

    const width = 272;
    const left = Math.max(8, Math.min(pos.left, window.innerWidth - width - 8));
    const arrowLeft = Math.max(8, (pos.left + pos.width / 2) - left - 6);

    const cancelHide = () => clearTimeout(hideTimer.current);
    const startHide = () => { hideTimer.current = setTimeout(() => setVisible(false), 150); };

    return createPortal(
        <>
            {/* Beacon dot — captures clicks so they don't fall through to the button */}
            <div
                className="mixpedia-beacon"
                style={{ top: pos.top - 4, left: pos.right - 4 }}
                onClick={(e) => { e.stopPropagation(); setVisible(v => !v); }}
            />
            {visible && (
                <div
                    ref={hintRef}
                    className="mixpedia-hint"
                    style={{ top: pos.bottom + 10, left, width }}
                    onMouseEnter={cancelHide}
                    onMouseLeave={autoShow ? undefined : startHide}
                >
                    <div className="mixpedia-hint__arrow" style={{ left: Math.max(8, arrowLeft) }} />
                    {title && <div className="mixpedia-hint__title">{title}</div>}
                    <p className="mixpedia-hint__message">{message}</p>
                    <div className="mixpedia-hint__actions">
                        {actionLabel && onAction && (
                            <button className="mixpedia-hint__dismiss mixpedia-hint__action" onClick={onAction}>{actionLabel}</button>
                        )}
                        <button className="mixpedia-hint__dismiss" onClick={onDismiss}>Got it</button>
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

export default MixpediaHint;
